import React from 'react';

import { SCALE_CSS_VAR, computeScale } from './scaleFactor';

/**
 * PLA-8844: keeps `--is-scale` on `ref` in step with that element's own width.
 *
 * Mounts nothing when `enabled` is false, so the flag-off screens carry no observer at all and the
 * property stays unset - which is what makes every `calc(<px> * var(--is-scale, 1))` resolve to the
 * drawn value and the screens render exactly as they did before this shipped.
 *
 * This CANNOT feed back into itself, and the BORDER box is what makes that true. The observed width
 * is set by the app layout and the Orbit panel beside it, both outside this subtree, and everything
 * the factor drives - font sizes, padding, radii, flex-basis floors capped at 100% of the column -
 * changes the content's HEIGHTS, never the element's own outer width.
 *
 * `contentRect` would NOT be safe here: it excludes padding, so the moment a scaled padding token
 * lands on the scope root or on anything that sets its content box, shrinking the padding would
 * widen the content box, which would raise the factor, which would widen the padding again. Reading
 * `borderBoxSize` breaks that circuit by measuring a quantity the factor cannot move. The scope root
 * also carries `min-width: 0`, without which a flex parent refuses to let the column shrink below
 * its own content and the factor could never engage.
 *
 * The write is committed on the next animation frame rather than inside the observer callback, for
 * the reason `components/Intelligence/useMeasuredWidth.ts` records: a synchronous write re-enters
 * layout while the browser is still delivering that batch of notifications, which is what produces
 * "ResizeObserver loop completed with undelivered notifications". An unchanged factor is not
 * written at all.
 */
const useIntelligenceScale = (ref: React.RefObject<HTMLElement>, enabled: boolean): void => {
  React.useEffect(() => {
    const node = ref.current;

    if (!enabled || !node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    let frame: number | null = null;
    // What was last written. Read here rather than off the element because `style.getPropertyValue`
    // returns the serialised string, which would make every comparison a parse.
    let committed: number | null = null;

    const commit = (width: number) => {
      const scale = computeScale(width);

      if (committed === scale) {
        return;
      }

      committed = scale;
      node.style.setProperty(SCALE_CSS_VAR, String(scale));
    };

    const observer = new ResizeObserver(entries => {
      const entry = entries[entries.length - 1];
      // borderBoxSize is an array per the spec and is what every engine above this repo's floor
      // reports; the rect is the fallback, and it is a border-box reading too.
      const measured =
        entry?.borderBoxSize?.[0]?.inlineSize ?? node.getBoundingClientRect().width ?? 0;

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      frame = requestAnimationFrame(() => {
        frame = null;
        commit(measured);
      });
    });

    observer.observe(node);

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }

      observer.disconnect();
      // The element outlives this effect whenever the flag is toggled at runtime, so the property
      // has to go with the observer or the screens would keep the last factor with nothing
      // maintaining it.
      node.style.removeProperty(SCALE_CSS_VAR);
    };
  }, [ref, enabled]);
};

export default useIntelligenceScale;
